import React from 'react';
import { getIntl } from '../../../../../utils/intl';
import * as Card from '../../../../v2BaseComponents/Card';
import { useCollaborators } from '../../../../../hooks/api/useCollaborators';
import SelectableMinimalCard, { NotSelectableProps, SelectableProps } from '../../../../shared/SelectableMinimalCard';
import {
    OrgOrProfileCareTeamMember,
    getCareTeamMemberDescriptor,
    isInsurance,
    isProvider,
    isSupport,
} from '../../../../../utils/careTeamUtils';
import getName from '../../../../../utils/getName';

const intl = getIntl('care-team-member-list-card', {
    pcp: 'PCP',
    support: 'Support',
    insurance: 'Insurance',
    provider: 'Provider',
    pluralCollaborators: '{count} Collaborators',
    singularCollaborators: '1 Collaborator',
});

export type CareTeamMemberListCardOwnProps = {
    careTeamMember: OrgOrProfileCareTeamMember;
    profileId: string | null;
    onNavigateToDetails?: () => void;
    flat?: boolean;
    testID?: string;
} & (SelectableProps | NotSelectableProps);

export type CareTeamMemberListCardProps = CareTeamMemberListCardOwnProps;
const CareTeamMemberListCard: React.FC<CareTeamMemberListCardProps> = (props) => {
    const { collaborators } = useCollaborators({
        medicalProfileId: props.profileId,
        careTeamMemberId: props.careTeamMember.careTeamMemberId,
    });

    const getHeaderText = () => {
        const descriptor = getCareTeamMemberDescriptor(props.careTeamMember);
        if (descriptor.type === 'text') {
            return descriptor.text;
        } else {
            return getName(descriptor);
        }
    };

    const getSubText = () => {
        if (isSupport(props.careTeamMember)) {
            return props.careTeamMember.contactDetails.role || intl('support');
        } else if (isProvider(props.careTeamMember)) {
            return props.careTeamMember.metadata.providerSpecialty || intl('provider');
        } else if (isInsurance(props.careTeamMember)) {
            return props.careTeamMember.metadata.insuranceRole || intl('insurance');
        } else {
            return '';
        }
    };

    const getDetailsText = () => {
        if (!collaborators?.length) {
            return props.careTeamMember.contactDetails.phoneNumber;
        } else {
            return collaborators.length === 1
                ? intl('singularCollaborators')
                : intl('pluralCollaborators', { count: collaborators.length });
        }
    };

    const mainText = getHeaderText();
    const secondaryText = getSubText();
    const subText = getDetailsText();

    return (
        <SelectableMinimalCard key={props.careTeamMember.careTeamMemberId} {...props}>
            <Card.CardContent>
                <Card.CardRightContent
                    rightContent={
                        'references' in props.careTeamMember ? (
                            <Card.CardReferenceCounts reference={props.careTeamMember.references} />
                        ) : null
                    }
                >
                    <Card.CardContent>
                        {mainText ? <Card.CardHeader>{mainText}</Card.CardHeader> : null}
                        {secondaryText ? <Card.CardBody>{secondaryText}</Card.CardBody> : null}
                        {subText ? <Card.CardBody2>{subText}</Card.CardBody2> : null}
                    </Card.CardContent>
                </Card.CardRightContent>
            </Card.CardContent>
        </SelectableMinimalCard>
    );
};

export default React.memo(CareTeamMemberListCard);
